import { useEffect } from "react";
import { useForm } from "react-hook-form";
import styled, { FormActions, TextInput, ClientForm, Button } from "livemap-ui";

import { useAddToast } from "../contexts/toast";
import { useDialingCodes } from "../hooks/dialing-codes";

import { DialingCode } from "./DialingCode";
import { useFocus } from "../hooks/focus";
import { FormProps } from "./FormProps";

export interface PhoneNumberProps {
  isSending: boolean;
  submitText: string;
}

export interface PhoneNumberData {
  dialingCode: string;
  cellPhone: string;
}

export interface PhoneNumberFormDispatch {
  onSubmit(form: PhoneNumberData): void;
}

type Props = PhoneNumberFormDispatch & PhoneNumberProps & FormProps;

export function CellPhoneForm({
  onSubmit,
  isSending,
  submitText,
  isActive,
}: Props) {
  const { register, handleSubmit, errors } = useForm<PhoneNumberData>({
    reValidateMode: "onSubmit",
  });
  const [focusRef, setInputFocus] = useFocus();
  const dialingCodes = useDialingCodes();
  const addToast = useAddToast();

  useEffect(() => {
    if (errors.cellPhone) {
      addToast({
        id: "cell-phone-error",
        type: "danger",
        title: "Invalid phone number",
        text: errors.cellPhone?.message || "",
      });
    }
  }, [addToast, errors.cellPhone]);

  useEffect(() => {
    isActive && setInputFocus();
  }, [isActive, setInputFocus]);

  useEffect(() => {
    if (errors.dialingCode) {
      addToast({
        id: "dialing-code-error",
        type: "danger",
        title: "Invalid phone number",
        text: errors.dialingCode?.message || "",
      });
    }
  }, [addToast, errors.dialingCode]);

  return (
    <ClientForm onSubmit={handleSubmit(onSubmit)}>
      <PhoneNumber>
        <DialingCode
          dialingCodes={dialingCodes}
          name="dialingCode"
          ref={register({
            required: "The country code is required",
            validate: (value) => {
              if (dialingCodes === null || dialingCodes[value]) {
                return undefined;
              } else {
                return `"${value}" is not a valid country code`;
              }
            },
          })}
        />

        <TextInput
          label="Phone number"
          name="cellPhone"
          ref={(htmlRef) => {
            focusRef.current = htmlRef;
            register({
              required: "Please enter a phone number.",
              minLength: {
                value: 6,
                message: "The phone number is too short.",
              },
              maxLength: {
                value: 20,
                message: "The phone number is too long.",
              },
            })(htmlRef);
          }}
        />
      </PhoneNumber>

      <FormActions>
        <Button disabled={isSending}>{submitText}</Button>
      </FormActions>
    </ClientForm>
  );
}

const PhoneNumber = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;
